import {
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from 'react-query';
import {
    createSigningSession,
    fetchSigningSession,
    forgetSigningLink,
    rememberSigningLink,
    revokeSigningSession,
    invoiceBuilderRequest,
    InvoiceBuilderApiError,
    isAppUnavailable,
} from './client';
import {
    InvoiceActivityListEnvelope,
    InvoiceContentBody,
    InvoiceCreateBody,
    InvoiceEnvelope,
    InvoiceInternalNoteBody,
    InvoiceListEnvelope,
    InvoicePatchBody,
    InvoiceRow,
    InvoiceSignatureInfo,
    InvoiceSignatureState,
    InvoiceSigningSessionEnvelope,
    InvoiceTemplateListEnvelope,
    ListInvoicesQuery,
} from '../types';
import {
    invoiceActivitiesPath,
    invoiceContentPath,
    invoiceInternalNotePath,
    invoicePath,
    invoicesPath,
    isSignatureRequested,
    isSigningLinkActive,
    templatesPath,
} from '../helpers';
import { INVOICE_ACTIVITY_PAGE_SIZE, INVOICE_PAGE_SIZE } from '../constants';

export { isAppUnavailable, InvoiceBuilderApiError };

export const useInvoiceList = (
    organizationId: string,
    query: ListInvoicesQuery,
    options?: UseQueryOptions<InvoiceListEnvelope, InvoiceBuilderApiError>,
) => {
    const pathname = invoicesPath(organizationId, {
        ...query,
        limit: query.limit || INVOICE_PAGE_SIZE,
    });
    const { enabled = true, ...rest } = options || {};
    return useQuery<InvoiceListEnvelope, InvoiceBuilderApiError>(
        ['invoice-builder', 'invoices', organizationId, query],
        () => invoiceBuilderRequest<InvoiceListEnvelope>('GET', pathname),
        {
            retry: false,
            ...rest,
            enabled: Boolean(organizationId) && enabled,
        },
    );
};

const invoiceQueryKey = (organizationId: string, invoiceId?: string) => [
    'invoice-builder',
    'invoice',
    organizationId,
    invoiceId,
];

export const useInvoice = (
    organizationId: string,
    invoiceId?: string,
    options?: UseQueryOptions<InvoiceEnvelope, InvoiceBuilderApiError>,
) => {
    const { enabled = true, ...rest } = options || {};
    return useQuery<InvoiceEnvelope, InvoiceBuilderApiError>(
        invoiceQueryKey(organizationId, invoiceId),
        () => invoiceBuilderRequest<InvoiceEnvelope>(
            'GET',
            invoicePath(organizationId, invoiceId as string),
        ),
        {
            retry: false,
            ...rest,
            enabled: Boolean(organizationId && invoiceId) && enabled,
        },
    );
};

export const useInvoiceTemplates = (
    organizationId: string,
    options?: UseQueryOptions<InvoiceTemplateListEnvelope, InvoiceBuilderApiError>,
) => {
    const { enabled = true, ...rest } = options || {};
    return useQuery<InvoiceTemplateListEnvelope, InvoiceBuilderApiError>(
        ['invoice-builder', 'templates', organizationId],
        () => invoiceBuilderRequest<InvoiceTemplateListEnvelope>(
            'GET',
            templatesPath(organizationId),
        ),
        {
            retry: false,
            refetchOnWindowFocus: false,
            ...rest,
            enabled: Boolean(organizationId) && enabled,
        },
    );
};

export const useCreateInvoice = (organizationId: string) => useMutation(
    (body: InvoiceCreateBody) => invoiceBuilderRequest<InvoiceEnvelope>(
        'POST',
        invoicesPath(organizationId),
        body,
    ),
);

export const signingSessionQueryKey = (organizationId: string, invoiceId: string) => [
    'invoice-builder',
    'signing-session',
    organizationId,
    invoiceId,
];

export const useInvoiceSigningSession = (
    organizationId: string,
    invoiceId?: string,
    enabled = true,
) => useQuery<InvoiceSigningSessionEnvelope, InvoiceBuilderApiError>(
    signingSessionQueryKey(organizationId, invoiceId || ''),
    () => fetchSigningSession(organizationId, invoiceId as string),
    {
        retry: false,
        enabled: Boolean(organizationId && invoiceId) && enabled,
    },
);

export interface InvoiceSigningCheck {
    editState?: InvoiceSignatureState | 'none';
    info?: InvoiceSignatureInfo;
}

export const useInvoiceSigningStates = (
    organizationId: string,
    invoices: InvoiceRow[],
) => {
    const invoiceResults = useQueries(
        invoices.map((invoice) => ({
            queryKey: invoiceQueryKey(organizationId, invoice.id),
            queryFn: () => invoiceBuilderRequest<InvoiceEnvelope>(
                'GET',
                invoicePath(organizationId, invoice.id),
            ),
            retry: false,
            enabled: Boolean(organizationId && invoice.id),
        })),
    );

    const signingResults = useQueries(
        invoices.map((invoice, index) => ({
            queryKey: signingSessionQueryKey(organizationId, invoice.id),
            queryFn: async () => {
                try {
                    return await fetchSigningSession(organizationId, invoice.id);
                } catch (error) {
                    if (
                        error instanceof InvoiceBuilderApiError
                        && (error.status === 404 || error.status === 410)
                    ) {
                        return null;
                    }
                    throw error;
                }
            },
            retry: false,
            enabled: Boolean(
                organizationId
                && invoice.id
                && invoice.status !== 'void'
                && isSignatureRequested(
                    invoiceResults[index]?.data?.data.document.design,
                ),
            ),
        })),
    );

    const checks: Record<string, InvoiceSigningCheck> = {};
    invoices.forEach((invoice, index) => {
        if (invoice.status === 'void') {
            checks[invoice.id] = { editState: 'none' };
            return;
        }

        const invoiceResult = invoiceResults[index];
        if (!invoiceResult?.isFetched || !invoiceResult.isSuccess) return;

        const design = invoiceResult.data?.data.document.design;
        if (!isSignatureRequested(design)) {
            checks[invoice.id] = { editState: 'none' };
            return;
        }

        const result = signingResults[index];
        if (!result?.isFetched || result.isError) return;

        const session = result.data?.data;
        const acceptance = session?.acceptance ?? null;
        let state: InvoiceSignatureState = 'unrequested';
        if (acceptance) state = 'signed';
        else if (session && isSigningLinkActive(session.expiresAt)) state = 'awaiting';
        const info: InvoiceSignatureInfo = {
            state,
            acceptance,
            locale: design?.regional.locale || 'en-US',
        };
        checks[invoice.id] = { editState: info.state, info };
    });
    return checks;
};

export const invoiceActivitiesQueryKey = (organizationId: string, invoiceId?: string) => [
    'invoice-builder',
    'activities',
    organizationId,
    invoiceId,
];

export const useInvoiceActivities = (
    organizationId: string,
    invoiceId?: string,
    enabled = true,
) => useInfiniteQuery<InvoiceActivityListEnvelope, InvoiceBuilderApiError>(
    invoiceActivitiesQueryKey(organizationId, invoiceId),
    ({ pageParam }) => invoiceBuilderRequest<InvoiceActivityListEnvelope>(
        'GET',
        invoiceActivitiesPath(organizationId, invoiceId as string, {
            cursor: typeof pageParam === 'string' ? pageParam : undefined,
            limit: INVOICE_ACTIVITY_PAGE_SIZE,
        }),
    ),
    {
        retry: false,
        enabled: Boolean(organizationId && invoiceId) && enabled,
        getNextPageParam: (lastPage) => lastPage.data.nextCursor || undefined,
    },
);

const refreshInvoiceActivities = (
    queryClient: ReturnType<typeof useQueryClient>,
    organizationId: string,
    invoiceId: string,
) => queryClient.invalidateQueries(invoiceActivitiesQueryKey(organizationId, invoiceId));

export const useCreateSigningSession = (organizationId: string) => {
    const queryClient = useQueryClient();
    return useMutation(async (invoiceId: string) => {
        const session = await createSigningSession(organizationId, invoiceId);
        const link = {
            url: session.data.url || '',
            expiresAt: session.data.expiresAt,
        };
        if (link.url) rememberSigningLink(organizationId, invoiceId, link);
        refreshInvoiceActivities(queryClient, organizationId, invoiceId);
        return link;
    });
};

export const useRevokeSigningSession = (organizationId: string) => {
    const queryClient = useQueryClient();
    return useMutation(async (invoiceId: string) => {
        const session = await revokeSigningSession(organizationId, invoiceId);
        forgetSigningLink(organizationId, invoiceId);
        refreshInvoiceActivities(queryClient, organizationId, invoiceId);
        return session;
    });
};

export const useUpdateInvoiceContent = (organizationId: string) => useMutation(
    ({ invoiceId, body }: {
        invoiceId: string;
        body: InvoiceContentBody;
    }) => invoiceBuilderRequest<InvoiceEnvelope>(
        'PATCH',
        invoiceContentPath(organizationId, invoiceId),
        body,
    ),
);

export const useUpdateInvoiceInternalNote = (organizationId: string) => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ invoiceId, body }: {
            invoiceId: string;
            body: InvoiceInternalNoteBody;
        }) => {
            const envelope = await invoiceBuilderRequest<InvoiceEnvelope>(
                'PATCH',
                invoiceInternalNotePath(organizationId, invoiceId),
                body,
            );
            queryClient.setQueryData<InvoiceEnvelope>(
                invoiceQueryKey(organizationId, invoiceId),
                (current) => ({
                    data: {
                        ...(current?.data || envelope.data),
                        ...envelope.data,
                        internalNote: body.internalNote,
                        document: {
                            ...(current?.data?.document || envelope.data.document),
                            ...envelope.data.document,
                            internalNote: body.internalNote,
                        },
                    },
                }),
            );
            return envelope;
        },
    );
};

export const usePatchInvoice = (organizationId: string) => useMutation(
    ({ invoiceId, body }: {
        invoiceId: string;
        body: InvoicePatchBody;
    }) => invoiceBuilderRequest<InvoiceEnvelope>(
        'PATCH',
        invoicePath(organizationId, invoiceId),
        body,
    ),
);
